import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Box, ButtonGroup, Button, Paper } from '@mui/material'
import ContentCopy from '@mui/icons-material/ContentCopy'
import ContentPasteSearchIcon from '@mui/icons-material/ContentPasteSearch'
import dayjs from 'dayjs'
import { useHistory } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import EditIcon from '@mui/icons-material/Edit'
import ColumnTable, { withValueAt } from '../shared/ColumnTable'
import request from '../utils/fetcher'
import PaginationToolbar from '../shared/PaginationToolbar'
import QuickFilters from '../shared/QuickFilters'
import ChangeStatusDialog from '../common/Parcel/ChangeStatusDialog'
import {
  getParcels,
  resetFilters,
  setPagination,
  setSorting,
  updateFilters,
} from '../redux/parcels/slice.parcels'
import { Line } from '../shared/Line'
import { useMe } from '../hooks/useMe'

const STATUSES = [
  'created',
  'on-way',
  'in-box',
  'marked-for-return',
  'deviation',
  'collected',
  'driver-picked-up',
  'cancelled',
  'pickup-on-way',
  'pickup-in-box',
  'pickup-ready-to-collect',
  'pickup-collected',
]

const DEVIATION_CODES = [
  'CustomReason',
  'FailToOpenLock',
  'FailToCloseLock',
  'NoParcelInLocker',
  'ParcelTooBig',
  'OtherParcelInLocker',
]

const lastDeviationCode = (row) =>
  [...(row.statuses || [])].reverse().find((status) => status?.data?.code)?.data
    ?.code || ''

function EllipsisText({ text }) {
  if (!text) return null
  return (
    <span
      title={text}
      style={{
        display: 'block',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}
    >
      {text}
    </span>
  )
}

/**
 * The handful of filters that sit beside the pager, and how many. Which ones is this person's
 * own — the scope the counter keeps them under, and the few that stand in until they have
 * filtered anything.
 */
const QUICK_FILTER_ID = 'parcelId'

/**
 * The carriers the carrier filter can name, by id.
 *
 * The pill beside the table names the carrier it filters on, and it reads that name from these
 * options. The list of carriers is fetched, so on a reload with a carrier filter already applied
 * the pill would show a bare _id until the request landed — or for good, if it failed. The loaded
 * parcels carry their carrier's name too, so those count as well and the carrier being filtered on
 * resolves whether or not the list arrives.
 *
 * The fetched list wins where both have a name: it is the whole list, and it is what the select
 * offers.
 */
export const carrierOptionsFrom = (fetched, cache) => {
  const byId = new Map((fetched || []).map((c) => [String(c.value), c.label]))
  Object.values(cache || {}).forEach((parcel) => {
    const id = parcel?.carrier && String(parcel.carrier)
    if (id && parcel.carrierInfo?.name && !byId.has(id)) {
      byId.set(id, parcel.carrierInfo.name)
    }
  })
  return [...byId].map(([value, label]) => ({ value, label }))
}

export default function ParcelsView() {
  const dispatch = useDispatch()
  const history = useHistory()
  const { t } = useTranslation('parcels')
  const { data, sorting, pagination, cache, loading } = useSelector(
    (state) => state.parcels
  )
  const { user, carrier } = useMe()
  const { filters } = useSelector((state) => state.parcels)
  const { carrierHeader } = useSelector((state) => state.root)
  const [fetchedCarriers, setFetchedCarriers] = useState([])
  const [countries, setCountries] = useState([])
  const [statusParcel, setStatusParcel] = useState(null)

  useEffect(() => {
    dispatch(getParcels())
  }, [dispatch, carrier])

  // Only the iBoxen carrier searches across carriers and countries
  useEffect(() => {
    if (!carrier.isIboxen) return
    const headers = { headers: { 'x-iboxen-carrier': carrierHeader } }
    request('GET', '/carrier/carriers', {}, headers)
      .then((list) =>
        setFetchedCarriers(
          (list || []).map((c) => ({ value: c._id, label: c.name }))
        )
      )
      .catch(() => setFetchedCarriers([]))
    request('GET', '/carrier/location-control/countries', {}, headers)
      .then(({ countries: found }) => setCountries(found || []))
      .catch(() => setCountries([]))
  }, [carrier.isIboxen, carrierHeader])

  const carrierOptions = useMemo(
    () => carrierOptionsFrom(fetchedCarriers, cache),
    [fetchedCarriers, cache]
  )

  const flows = useMemo(() => {
    const f = ['delivery']
    if (carrier.isAllowedTo('usePickups') || user.isAllowedTo('usePickups'))
      f.push('pickup')
    return f
  }, [carrier, user])

  const refetch = useCallback(() => {
    dispatch(setPagination({ page: 0 }))
    dispatch(getParcels())
  }, [dispatch])

  // Nothing ranks the filters any more — the parcel id has the one permanent
  // slot — so reaching for a filter is no longer counted. The /me/usage scope it
  // wrote to is still there for the menu.
  const applyFilter = useCallback(
    (path, value) => {
      dispatch(updateFilters(withValueAt(filters, path, value)))
      refetch()
    },
    [dispatch, filters, refetch]
  )

  const columns = useMemo(
    () => [
      {
        id: 'flow',
        type: String,
        label: t('type'),
        sortable: true,
        render: (row) => t(`flows.${row.flow ? row.flow : 'delivery'}`),
        width: 85,
        filter: {
          kind: 'select',
          path: 'flow',
          options: flows,
          translate: 'parcels:flows',
        },
      },
      {
        id: 'parcelId',
        type: String,
        label: t('parcelId'),
        sortable: true,
        render: (row) => (
          <ButtonGroup
            size="small"
            variant="outlined"
            fullWidth
            sx={{ width: '100%' }}
          >
            <Button
              onClick={() => {
                history.push(`/carrier/parcels/${row._id}`)
              }}
              fullWidth
            >
              <span
                style={{
                  textWrap: 'nowrap',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  textTransform: 'none',
                  maxWidth: '100%',
                }}
              >
                {row.parcelId}
              </span>
            </Button>
            <Button
              onClick={() => navigator.clipboard.writeText(row.parcelId)}
              sx={{ px: 0 }}
              style={{ minWidth: 0, width: 35 }}
            >
              <ContentCopy sx={{ fontSize: 15 }} />
            </Button>
          </ButtonGroup>
        ),
        width: 270,
        filter: { kind: 'text', path: 'parcelId' },
      },
      {
        id: 'created',
        type: Date,
        label: t('created'),
        sortable: true,
        render: (row) => dayjs(row.created || row.createdAt).format('L LT'),
        width: 145,
        filter: { kind: 'dateRange', path: 'created' },
      },
      {
        id: 'currentStatus',
        type: String,
        label: t('status'),
        sortable: true,
        // The status column is where someone notices a parcel is on the wrong one, so it opens
        // the change straight away. The click must not also open the parcel behind it.
        render: (row) => (
          <Button
            size="small"
            color="inherit"
            // the pen says the cell is a way in, not just a value
            endIcon={<EditIcon sx={{ fontSize: 14, opacity: 0.6 }} />}
            sx={{
              textTransform: 'none',
              p: 0,
              minWidth: 0,
              justifyContent: 'flex-start',
              textAlign: 'left',
            }}
            onClick={(e) => {
              e.stopPropagation()
              setStatusParcel(row)
            }}
          >
            {t(`statuses.${row.currentStatus}`)}
          </Button>
        ),
        width: 200,
        filter: {
          kind: 'multiSelect',
          path: 'currentStatus',
          options: STATUSES,
          translate: 'parcels:statuses',
        },
      },
      {
        id: 'currentStatusCreated',
        type: Date,
        label: t('statusDate'),
        sortable: true,
        render: (row) => dayjs(row.currentStatusCreated).format('L LT'),
        width: 145,
        filter: { kind: 'dateRange', path: 'currentStatusCreated' },
      },
      // Early, next to the status: how much a driver did at the box is the first thing support looks at
      {
        id: 'driverLogs',
        type: String,
        label: t('driverLogs'),
        sortable: true,
        sortBy: 'driverLogCount',
        render: (row) => row.driverLogCount || '',
        width: 120,
        hidden: !carrier.isIboxen,
        filter: { kind: 'number', path: 'driverLogCount' },
      },
      {
        id: 'locationAddress',
        type: String,
        label: t('address'),
        sortable: true,
        render: (row) =>
          row.location?.address?.street && (
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                dispatch({
                  type: 'setShowModal',
                  payload: {
                    modal: 'location',
                    selectedLocation: row.location._id,
                  },
                })
              }}
              fullWidth
            >
              <span
                style={{
                  textWrap: 'nowrap',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  textTransform: 'none',
                  maxWidth: '100%',
                }}
              >
                {row.location?.address?.street}
              </span>
            </Button>
          ),
        width: 250,
        filter: {
          kind: 'text',
          path: 'location.street',
          ...(carrier.isIboxen && { suggest: 'location.street' }),
        },
      },
      {
        id: 'locationZip',
        type: String,
        label: t('zip'),
        sortable: true,
        render: (row) => row.location?.address?.zip,
        width: 75,
        filter: {
          kind: 'text',
          path: 'location.zip',
          ...(carrier.isIboxen && { suggest: 'location.zip' }),
        },
      },
      {
        id: 'locationCity',
        type: String,
        label: t('city'),
        sortable: true,
        render: (row) => row.location?.address?.city,
        width: 140,
        filter: {
          kind: 'text',
          path: 'location.city',
          ...(carrier.isIboxen && { suggest: 'location.city' }),
        },
      },
      {
        id: 'compartment',
        type: String,
        label: t('compartment'),
        sortable: true,
        width: 120,
        // Reads "3 A": the compartment and the locker's mark. Either half filters on its own,
        // and the server takes them in any order — "3", "A", "3 A", "3a" or "A3".
        filter: { kind: 'text', path: 'compartment' },
      },
      // Support columns, shown to the iBoxen carrier only (carriers keep their current table)
      {
        id: 'carrier',
        type: String,
        label: t('carrier'),
        sortable: true,
        render: (row) => <EllipsisText text={row.carrierInfo?.name} />,
        width: 140,
        hidden: !carrier.isIboxen,
        filter: { kind: 'select', path: 'carrierId', options: carrierOptions },
      },
      // These six had no column of their own and lived in the filter bar. They are columns now so
      // their filters have somewhere to hang, and start hidden so the table is no wider than before.
      {
        id: 'receiverFirstName',
        type: String,
        label: t('receiverFirstName'),
        sortable: true,
        render: (row) => <EllipsisText text={row.receiver?.firstName} />,
        width: 150,
        hidden: !carrier.isIboxen,
        defaultHidden: true,
        filter: { kind: 'text', path: 'receiverFirstName' },
      },
      {
        id: 'receiverLastName',
        type: String,
        label: t('receiverLastName'),
        sortable: true,
        render: (row) => <EllipsisText text={row.receiver?.lastName} />,
        width: 150,
        hidden: !carrier.isIboxen,
        defaultHidden: true,
        filter: { kind: 'text', path: 'receiverLastName' },
      },
      {
        id: 'phone',
        type: String,
        label: t('phone'),
        sortable: true,
        render: (row) => <EllipsisText text={row.receiver?.phoneNumber} />,
        width: 150,
        hidden: !carrier.isIboxen,
        defaultHidden: true,
        filter: { kind: 'text', path: 'phone' },
      },
      {
        id: 'objectId',
        type: String,
        label: t('internalId'),
        sortable: true,
        render: (row) => <EllipsisText text={row._id} />,
        width: 220,
        hidden: !carrier.isIboxen,
        defaultHidden: true,
        filter: { kind: 'text', path: 'objectId' },
      },
      {
        id: 'country',
        type: String,
        label: t('country'),
        sortable: true,
        render: (row) => row.location?.address?.country || 'SE',
        width: 90,
        hidden: !carrier.isIboxen,
        defaultHidden: true,
        filter: {
          kind: 'select',
          path: 'location.country',
          options: countries,
        },
      },
      {
        id: 'deviationCode',
        type: String,
        label: t('deviationCode'),
        sortable: true,
        render: (row) => lastDeviationCode(row),
        width: 170,
        defaultHidden: true,
        filter: {
          kind: 'select',
          path: 'deviationCode',
          options: DEVIATION_CODES,
          translate: 'parcels:deviationCodes',
        },
      },
      {
        id: 'statusChangedBy',
        type: String,
        label: t('statusChangedBy'),
        sortable: true,
        render: (row) => <EllipsisText text={row.statusChangedBy} />,
        width: 160,
        hidden: !carrier.isIboxen,
        filter: { kind: 'text', path: 'statusChangedBy' },
      },
      {
        id: 'lastError',
        type: String,
        label: t('lastError'),
        sortable: true,
        render: (row) => <EllipsisText text={row.lastError} />,
        width: 160,
        hidden: !carrier.isIboxen,
        filter: { kind: 'text', path: 'lastError' },
      },
      {
        id: 'trace',
        type: String,
        label: t('trace'),
        sortable: false,
        render: (row) => (
          <Box>
            {/* The click used to sit on the icon, so the button around it — its padding, its
                edges — did nothing at all. It belongs on the button. */}
            <Button
              variant="contained"
              size="small"
              aria-label={t('trace')}
              sx={{ px: 1 }}
              style={{ minWidth: 0 }}
              onClick={() =>
                dispatch({
                  type: 'setShowModal',
                  payload: { modal: 'traceLogs', selectedParcel: row._id },
                })
              }
            >
              <ContentPasteSearchIcon fontSize="small" />
            </Button>
          </Box>
        ),
        width: 70,
        hidden: !user.isAllowedTo('viewTraceLogs'),
      },
    ],
    [dispatch, history, t, user, carrier, flows, carrierOptions, countries]
  )

  // The parcel id is the one field worth a permanent place beside the pager. Every
  // other filter still lives on its column and is one click away in the column's
  // own menu — a row of three was a second filter bar in all but name.
  const quickFilterColumns = columns.filter(
    (column) => column.id === QUICK_FILTER_ID && !column.hidden
  )

  if (!user || !carrier) return <p>{t('loading')}</p>

  // The same toolbar sits above and below the table. The quick filters belong with the one you
  // read before searching; repeating them under the rows made the foot of the table a second
  // filter bar, which is what this was meant to get rid of.
  const toolbar = (withQuickFilters) => (
    <PaginationToolbar
      spacing={0.5}
      quickFilters={
        withQuickFilters ? (
          <QuickFilters
            columns={quickFilterColumns}
            filters={filters}
            onFilter={applyFilter}
          />
        ) : undefined
      }
      count={data.metadata?.totalCount || 0}
      rowsPerPageOptions={[10, 20, 40, 100]}
      rowsPerPage={pagination.perPage}
      page={pagination.page}
      onPageChange={(_, page) => {
        dispatch(setPagination({ page }))
        dispatch(getParcels())
      }}
      onRowsPerPageChange={(e) => {
        dispatch(setPagination({ perPage: parseInt(e.target.value, 10) }))
        dispatch(getParcels())
      }}
    />
  )

  return (
    <>
      <Paper sx={{ background: '#e8e0d8', pb: 0.5, mb: 2 }}>
        {toolbar(true)}
        <Line mt={0.5} mb={0} />
        <ColumnTable
          columns={columns}
          rows={data.parcels.map(({ _id }) => cache[_id]).filter(Boolean)}
          storageKey="parcels"
          loading={loading}
          filters={filters}
          onFilter={applyFilter}
          onClearFilters={() => {
            dispatch(resetFilters())
            refetch()
          }}
          sorting={sorting}
          onSort={(next) => {
            dispatch(setSorting(next))
            refetch()
          }}
        />
        {toolbar(false)}
      </Paper>
      <ChangeStatusDialog
        parcel={statusParcel}
        open={Boolean(statusParcel)}
        onClose={() => setStatusParcel(null)}
        onChanged={() => {
          setStatusParcel(null)
          dispatch(getParcels())
        }}
      />
    </>
  )
}
